<script setup lang="ts">
  import { computed } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import type { AiChatQuestionTemplate } from '@/types/generated/types.gen'

  const props = defineProps<{
    firstName?: string
    lastName?: string
    templates: AiChatQuestionTemplate[]
    showAllLabel: string
  }>()

  defineEmits<{
    (e: 'select', uuid: string): void
    (e: 'showAll'): void
  }>()

  const { t } = useTranslation({
    en: {
      hello: 'Hello',
      howCanIHelp: 'how can I help you?'
    },
    de: {
      hello: 'Hallo',
      howCanIHelp: 'wie kann ich Ihnen helfen?'
    },
    fr: {
      hello: 'Bonjour',
      howCanIHelp: 'comment puis-je vous aider ?'
    }
  })

  const displayName = computed(() =>
    [props.firstName?.trim(), props.lastName?.trim()].filter(Boolean).join(' ')
  )
</script>

<template>
  <div class="flex flex-1 flex-col justify-end gap-4 px-4 pb-4">
    <div class="flex flex-col items-start self-stretch">
      <p class="text-xl font-medium leading-8 tracking-normal text-gray-300 font-inter">
        {{ t('hello') }}
        <template v-if="displayName">{{ displayName }},</template>
      </p>
      <p
        class="bg-gradient-to-r from-[rgb(239,70,154)] to-[rgb(147,51,234)] bg-clip-text text-xl font-medium
          leading-8 tracking-normal text-transparent font-inter"
      >
        {{ t('howCanIHelp') }}
      </p>
    </div>

    <div class="inline-flex flex-col items-start gap-3">
      <button
        v-for="tmpl in templates"
        :key="tmpl.uuid"
        type="button"
        class="inline-flex max-w-full items-center gap-2.5 rounded-lg bg-purple-50 px-2 py-1.5 text-left transition
          hover:bg-purple-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
          focus-visible:outline-purple-600"
        @click="$emit('select', tmpl.uuid)"
      >
        <span class="truncate text-sm font-normal leading-5 text-gray-800 font-inter">
          {{ tmpl.title }}
        </span>
      </button>
      <button
        v-if="templates.length > 0"
        type="button"
        class="inline-flex items-center gap-2.5 rounded-lg bg-purple-50 px-2 py-1.5 text-left transition
          hover:bg-purple-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
          focus-visible:outline-purple-600"
        @click="$emit('showAll')"
      >
        <span class="line-clamp-1 text-sm font-normal leading-5 text-gray-800 font-inter">
          {{ showAllLabel }}
        </span>
      </button>
    </div>
  </div>
</template>
